// src/pages/Achievements.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "../styles/Achievements.css";

import certificateFrame from "../assets/ac-frame.png";
import Footer from "../components/Footer";

// Firestore
import { db } from "../firebase";
import { collection, doc, getDoc, getDocs, orderBy, query } from "firebase/firestore";

// Tabs
const TABS = [
  { key: "services", label: "Services" },
  { key: "handmade", label: "Handmade Gifts" },
];

// Try ordered, fall back to unordered if "order" field doesn't exist
async function readMaybeOrdered(colRef) {
  try {
    const qOrdered = query(colRef, orderBy("order", "asc"));
    return await getDocs(qOrdered);
  } catch {
    return await getDocs(colRef);
  }
}

// Coerce a Firestore row into the shape we render
function coerceRow(raw) {
  const type = raw.type ?? raw.category ?? raw.group ?? "services";
  const order =
    typeof raw.order === "number"
      ? raw.order
      : Number.isFinite(Number(raw.order))
      ? Number(raw.order)
      : 9999;

  return {
    id: raw.id,
    title: raw.title || "",
    image: raw.image || "",
    type,
    order,
    active: raw.active !== false,
  };
}

/**
 * Geometry of the window/title INSIDE the PNG frame at a 300px-wide design.
 * We keep this so percent math stays correct when we push CSS vars.
 */
const GEOM_300 = {
  imgLeft: 19,
  imgWidth: 263,
  imgTop: 12,
  imgHeight: 151,
  titleSide: 20,
  titleBottom: 12,
  baseWidth: 300,
};

const Achievements = () => {
  // Page settings (hero)
  const [page, setPage] = useState({
    heroTitle: "Achievements",
    heroSubtitle:
      "A glimpse into the milestones, moments, and memories that shaped my journey.",
    heroBackground: "",
  });

  // Data
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("services");
  const [loading, setLoading] = useState(true);

  // frame natural size (loaded once)
  const natSize = useRef({ w: 0, h: 0 });

  const debug =
    typeof window !== "undefined" &&
    /[?&]debug=1/.test(window.location.search);

  // Load page + items
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const pageRef = doc(db, "pageContent", "achievements");
        const pageSnap = await getDoc(pageRef);
        if (mounted && pageSnap.exists()) {
          const d = pageSnap.data() || {};
          setPage((p) => ({
            heroTitle: d.heroTitle || p.heroTitle,
            heroSubtitle: d.heroSubtitle || p.heroSubtitle,
            heroBackground: d.heroBackground || p.heroBackground,
          }));
        }

        const col = collection(db, "achievements");
        const snap = await readMaybeOrdered(col);
        if (!mounted) return;

        const rowsRaw = [];
        snap.forEach((doc) => rowsRaw.push({ id: doc.id, ...doc.data() }));

        const rows = rowsRaw
          .map(coerceRow)
          .filter((r) => r.title && r.active);

        rows.sort((a, b) =>
          a.order !== b.order
            ? a.order - b.order
            : String(a.title).localeCompare(String(b.title))
        );

        if (debug) {
          console.clear();
          console.log("[Achievements] fetched:", rows.length);
          rows.forEach((r) =>
            console.log(`• ${r.id} | type: ${r.type} | title: ${r.title}`)
          );
        }

        setItems(rows);
      } catch (e) {
        console.error("Achievements: load failed", e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [debug]);

  // Compute and apply CSS vars to a .frame-wrap based on its current size
  const applyVarsToWrap = useCallback((wrapEl) => {
    if (!wrapEl) return;
    const { w: natW, h: natH } = natSize.current;
    if (!natW || !natH) return;

    const W = wrapEl.clientWidth;
    const H = wrapEl.clientHeight;

    const designH_at_300 = natH * (GEOM_300.baseWidth / natW);

    // Ratios relative to frame WIDTH
    const rLeft = GEOM_300.imgLeft / GEOM_300.baseWidth;
    const rWidth = GEOM_300.imgWidth / GEOM_300.baseWidth;
    const rTitleSide = GEOM_300.titleSide / GEOM_300.baseWidth;

    // Ratios relative to frame HEIGHT
    const rTop = GEOM_300.imgTop / designH_at_300;
    const rHeight = GEOM_300.imgHeight / designH_at_300;
    const rBottom = GEOM_300.titleBottom / designH_at_300;

    // Convert to actual pixels for THIS wrap size
    const imgLeft = W * rLeft;
    const imgWidth = W * rWidth;
    const imgTop = H * rTop;
    const imgHeight = H * rHeight;

    const titleLeft = W * rTitleSide;
    const titleRight = W * rTitleSide;
    const titleBottom = H * rBottom;

    wrapEl.style.setProperty("--img-left", `${imgLeft}px`);
    wrapEl.style.setProperty("--img-width", `${imgWidth}px`);
    wrapEl.style.setProperty("--img-top", `${imgTop}px`);
    wrapEl.style.setProperty("--img-height", `${imgHeight}px`);
    wrapEl.style.setProperty("--title-left", `${titleLeft}px`);
    wrapEl.style.setProperty("--title-right", `${titleRight}px`);
    wrapEl.style.setProperty("--title-bottom", `${titleBottom}px`);
  }, []);

  // Apply to all cards (on first paint and on resize)
  const updateAllCardVars = useCallback(() => {
    const wraps = document.querySelectorAll(".frame-wrap");
    wraps.forEach((w) => applyVarsToWrap(w));
  }, [applyVarsToWrap]);

  // Load the frame PNG once to know its natural aspect ratio (needed for correct % math)
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      natSize.current = { w: img.naturalWidth || 0, h: img.naturalHeight || 0 };
      updateAllCardVars(); // after we know the natural size, compute positions once
    };
    img.src = certificateFrame;
  }, [updateAllCardVars]); // ✅ ESLint-safe

  // Keep variables fresh on resize or when grid changes
  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      entries.forEach((entry) => applyVarsToWrap(entry.target));
    });
    const wraps = document.querySelectorAll(".frame-wrap");
    wraps.forEach((w) => ro.observe(w));
    return () => ro.disconnect();
  }, [items, applyVarsToWrap]);

  // Filter for current tab
  const filtered = useMemo(
    () => items.filter((i) => (i.type || "services") === tab),
    [items, tab]
  );

  const bgStyle = page.heroBackground
    ? { backgroundImage: `url(${page.heroBackground})` }
    : {};

  return (
    <div className="achievements-container">
      {debug && (
        <div style={{ padding: "6px 12px", fontSize: 12, opacity: 0.7 }}>
          debug: items={items.length} · tab="{tab}"
        </div>
      )}

      {/* Hero */}
      <div className="achievements-hero" style={bgStyle}>
        <div className="achievements-hero-overlay" />
        <div className="achievements-hero-text">
          <h1>{page.heroTitle}</h1>
          <p>{page.heroSubtitle}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="category-toggle">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={tab === t.key ? "active" : ""}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Grid */}
      <div className="achievement-grid">
        {loading ? (
          <>
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="achievement-card"
                style={{
                  border: "2px solid #000",
                  borderRadius: 6,
                  background:
                    "linear-gradient(90deg,#eee 25%,#f6f6f6 37%,#eee 63%)",
                  backgroundSize: "400% 100%",
                  animation: "t-shimmer 1.2s infinite",
                }}
              />
            ))}
          </>
        ) : filtered.length === 0 ? (
          <div className="fallback-message">
            No milestones on the board… yet! <br />
            Stay tuned for some awesome achievements!
          </div>
        ) : (
          filtered.map((item) => (
            <Card key={item.id} item={item} onFrameLoad={updateAllCardVars} />
          ))
        )}
      </div>

      <Footer />
    </div>
  );
};

/** Per-card: keeps image geometry fixed and auto-fits the title into max 2 lines. */
function Card({ item, onFrameLoad }) {
  const frameRef = useRef(null);
  const titleRef = useRef(null);

  // Auto-fit title into at most 2 lines by gently shrinking the font if needed,
  // but never smaller than MIN_FONT. If it still overflows, clamp with ellipsis.
  useEffect(() => {
    if (!titleRef.current) return;

    const el = titleRef.current;
    const MAX_LINES = 2;
    const SHRINK_STEP = 0.5; // px
    const viewportW = typeof window !== "undefined" ? window.innerWidth : 9999;
    const MIN_FONT = viewportW <= 360 ? 13 : 14; // requirement

    const fit = () => {
      el.style.fontSize = "";
      el.classList.remove("is-clamped");
      el.removeAttribute("title");

      const compute = () => {
        const cs = getComputedStyle(el);
        const fs = parseFloat(cs.fontSize) || 16;
        const lh = parseFloat(cs.lineHeight) || fs * 1.3;
        return { fs, maxH: MAX_LINES * lh };
      };

      let { fs, maxH } = compute();
      let guard = 160;

      while (el.scrollHeight > maxH && fs > MIN_FONT && guard-- > 0) {
        fs = fs - SHRINK_STEP;
        el.style.fontSize = fs + "px";
        ({ fs, maxH } = compute());
      }

      if (el.scrollHeight > maxH) {
        el.classList.add("is-clamped");
        if (!el.getAttribute("title")) {
          el.setAttribute("title", el.textContent || "");
        }
      }
    };

    fit();

    const onResize = () => fit();
    window.addEventListener("resize", onResize);

    const ready = document.fonts?.ready;
    if (ready && ready.then) ready.then(fit).catch(() => {});

    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, [item.title]);

  return (
    <div className="achievement-card">
      <div className="frame-wrap" ref={frameRef}>
        <img
          src={certificateFrame}
          alt="frame"
          className="certificate-frame"
          onLoad={onFrameLoad}
        />
        <div
          className="certificate-image"
          style={{
            backgroundImage: item.image ? `url(${item.image})` : undefined,
          }}
        />
        <p className="certificate-title" ref={titleRef}>
          {item.title}
        </p>
      </div>
    </div>
  );
}

export default Achievements;
